import { apiStatic, requestError } from 'AurionCR/components';
import { notifyRequestResult } from 'AurionCR/store/modules/notify';
import { pathStepCommittee } from 'components/rating-process-preview/step-committee';
import { pathStepDone } from 'components/rating-process-preview/step-done';
import { pathStepForecast } from 'components/rating-process-preview/step-forecast';
import { pathStepInformationRequest } from 'components/rating-process-preview/step-information-request';
import { pathStepPublication } from 'components/rating-process-preview/step-publication';
import { pathStepReport } from 'components/rating-process-preview/step-report';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { API_RATING_PROCESS_HELPER } from 'services/rating-process-helper';
import { iRPState, iRPStepDoneActionTypes, iRPStepDoneFinishAction } from '../@type';
import { RPMainUpdateStep, RPMergeMain, RPStepDoneMerge } from '../helpers';
import { API_RATING_PROCESS } from 'services/rating-process';
import {
  parseGetUrlToPost,
  prepareRequest,
} from 'components/helpers';
import { parseMixins, saveMixins } from 'AurionCR/components/formV2';
import { generateAndSavePdf } from '../../../pdf-generator';
import { getLabelsPath, labelsKey } from 'components/language/i18n-context-provider';

function* stepData({ path, step }: any): any {
  if (step.isCompleted) {
    const {
      data: { value },
    } = yield call(apiStatic.post, ...parseGetUrlToPost(String(path)));
    return {
      step,
      value: value[0],
    };
  } else {
    return {
      step,
      value: null,
    };
  }
}

function* getData() {
  const { main }: iRPState = yield select((state) => state.RP);
  if (main?.id) {
    try {
      const data: any[] = yield all(
        main.ratingProcessSteps
          .map((step) => {
            switch (step.ratingProcessStepTypeID) {
              case 1:
                return { step, path: pathStepInformationRequest(main.id) };
              case 2:
                return { step, path: pathStepForecast(step.id) };
              case 3:
                return { step, path: pathStepCommittee(step.id) };
              case 4:
                return { step, path: pathStepReport(step.id) };
              case 5:
                return { step, path: pathStepPublication(step.id) };
              case 1000:
                return { step, path: pathStepDone(main.id) };
              default:
                return undefined;
            }
          })
          .filter((value) => value)
          .map((value) => call(stepData, value)),
      );
      yield put(RPStepDoneMerge({ isInit: true, isLoading: false, data }));
    } catch (e) {
      yield put(notifyRequestResult(requestError(e), 'error'));
    }
  }
}

function* init() {
  const {
    stepDone: { isInit, isLoading },
  }: iRPState = yield select((state) => state.RP);
  if (!isInit && !isLoading) {
    yield put(RPStepDoneMerge({ isLoading: true }));
    yield call(getData);
  }
}

export const base64ToFileStream = (base64: string) => {
  return base64.split(',')[1];
};

function* finish({ payload }: iRPStepDoneFinishAction) {
  const { main, currentStep }: iRPState = yield select((state) => state.RP);
  yield put(RPStepDoneMerge({ isLoading: true }));

  try {
    yield call(() => saveForm({ payload, type: iRPStepDoneActionTypes.RP_STEP_DONE_FINISH }));
    
    // Generate and save PDF (completely independent of Redux store)
    // @ts-ignore
    const { sources, language }: any = yield select((state) => state);
    const labels = sources[labelsKey]?.[getLabelsPath(language.languageID)]?.map || {};
    yield call(generateAndSavePdf, Number(main?.id), (key?: string) => labels[key as string]?.title || key || '');
    
    // Complete rating process
    if (main?.ratingProcessStatusID === 4) {
      yield call(
        apiStatic.get,
        API_RATING_PROCESS_HELPER.FINISH_RATING_PROCESS_FOR_UPDATE(Number(main?.id)),
      );
    } else {
      yield call(apiStatic.get, API_RATING_PROCESS_HELPER.FINISH_RATING_PROCESS(Number(main?.id)));
    }
    yield put(RPMainUpdateStep({ stepIndex: currentStep, isCompleted: true, isStepEdit: false }));
    yield put(RPStepDoneMerge({ isLoading: false }));
    yield put(RPMergeMain({ isCompleted: true, isRPCompleted: true, ratingProcessStatusID: 3 }));
  } catch (e) {
    yield put(notifyRequestResult(requestError(e), 'error'));
    yield put(RPStepDoneMerge({ isLoading: false }));
  }
}

function* saveForm({ payload }: iRPStepDoneFinishAction) {
  const { main }: iRPState = yield select((state) => state.RP);
  try {
    if (main?.id) {
      let { data, mixins } = parseMixins(payload);
      // @ts-ignore
      data = yield call(saveMixins, data, mixins);
      yield call(apiStatic.patch, API_RATING_PROCESS.PATCH({ id: main.id }), prepareRequest(data));
    }
  } catch (e) {
    yield put(notifyRequestResult(requestError(e), 'error'));
  }
}

function* submitPreviewDoneForm({ payload }: iRPStepDoneFinishAction) {
  yield put(RPStepDoneMerge({ isLoading: true }));
  try {
    yield call(() => saveForm({ payload, type: iRPStepDoneActionTypes.RP_STEP_DONE_FINISH }));
    yield call(getData);
  } catch (e) {
    yield put(notifyRequestResult(requestError(e), 'error'));
  }
  yield put(RPStepDoneMerge({ isLoading: false }));
}

export default [
  takeLatest(iRPStepDoneActionTypes.RP_STEP_DONE_INIT, init),
  takeLatest(iRPStepDoneActionTypes.RP_STEP_DONE_FINISH, finish),
  takeLatest(iRPStepDoneActionTypes.RP_STEP_SUBMIT_FORM, submitPreviewDoneForm),
];
